import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Gift, Copy, Check, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface PopupData {
  id: string;
  is_enabled: boolean;
  title: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  use_coupon_validity: boolean;
  discount_code: {
    id: string;
    code: string;
    discount_type: string;
    discount_value: number;
    valid_from: string | null;
    valid_until: string | null;
    max_uses: number | null;
    times_used: number;
  } | null;
}

export function HomepagePopup() {
  const [open, setOpen] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    checkAndShowPopup();
  }, []);

  const checkAndShowPopup = async () => {
    // Check if user has dismissed popup in this session
    const dismissed = sessionStorage.getItem("homepage_popup_dismissed");
    if (dismissed) return;

    // Check if user already received this offer via email (stored in localStorage)
    const emailedOffers = localStorage.getItem("popup_emailed_offers");
    
    // Fetch popup settings
    const { data: settings, error } = await supabase
      .from("platform_popup_settings")
      .select("*")
      .eq("is_enabled", true)
      .limit(1)
      .single();

    if (error || !settings || !settings.discount_code_id) return;

    // Check if this popup was already emailed to user
    if (emailedOffers) {
      const offers = JSON.parse(emailedOffers);
      if (offers.includes(settings.id)) return;
    }

    // Fetch the associated discount code via the public validator RPC
    const { data: codeData, error: codeError } = await supabase
      .rpc("lookup_platform_discount_code", { _id: settings.discount_code_id })
      .maybeSingle();

    if (codeError || !codeData) return;

    const now = new Date();

    // Check validity based on settings
    if (settings.use_coupon_validity) {
      // Use coupon's validity period
      if (codeData.valid_from && new Date(codeData.valid_from) > now) return;
      if (codeData.valid_until && new Date(codeData.valid_until) < now) return;
      // Check max uses
      if (codeData.max_uses && codeData.times_used >= codeData.max_uses) return;
    } else {
      // Use popup's custom date range
      if (settings.start_date && new Date(settings.start_date) > now) return;
      if (settings.end_date && new Date(settings.end_date) < now) return;
    }

    setPopupData({
      ...settings,
      discount_code: codeData
    } as PopupData);
    
    // Increment display count
    await supabase
      .from("platform_popup_settings")
      .update({ times_displayed: (settings.times_displayed || 0) + 1 })
      .eq("id", settings.id);
    
    // Small delay before showing popup
    setTimeout(() => setOpen(true), 1500);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("homepage_popup_dismissed", "true");
    setOpen(false);
  };

  const handleCopyCode = () => {
    if (popupData?.discount_code?.code) {
      navigator.clipboard.writeText(popupData.discount_code.code);
      setCopied(true);
      toast.success("Code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEmailOffer = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!popupData?.discount_code) return;

    setSendingEmail(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-popup-offer", {
        body: {
          email,
          popupId: popupData.id,
          discountCodeId: popupData.discount_code.id,
          discountCode: popupData.discount_code.code,
          discountValue: popupData.discount_code.discount_value,
          discountType: popupData.discount_code.discount_type,
          popupTitle: popupData.title,
          popupDescription: popupData.description || ""
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
      } else {
        // Store that this popup was emailed to prevent showing again
        const emailedOffers = localStorage.getItem("popup_emailed_offers");
        const offers = emailedOffers ? JSON.parse(emailedOffers) : [];
        offers.push(popupData.id);
        localStorage.setItem("popup_emailed_offers", JSON.stringify(offers));

        setEmailSent(true);
        toast.success("Offer sent to your email!");
        
        // Close popup after delay
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      }
    } catch (error: any) {
      console.error("Failed to send email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!popupData || !popupData.discount_code) return null;

  const discountText = popupData.discount_code.discount_type === "percentage"
    ? `${popupData.discount_code.discount_value}% OFF`
    : `£${popupData.discount_code.discount_value} OFF`;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Gift className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold text-center">
            {popupData.title}
          </DialogTitle>
          {popupData.description && (
            <DialogDescription className="text-center text-base mt-2">
              {popupData.description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-center">
            <Badge variant="secondary" className="text-lg px-4 py-2 bg-primary/10 text-primary border-primary/20">
              {discountText}
            </Badge>
          </div>

          <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
            <div className="flex-1 text-center">
              <p className="text-sm text-muted-foreground mb-1">Use code:</p>
              <p className="text-2xl font-mono font-bold tracking-wider">
                {popupData.discount_code.code}
              </p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyCode}
              className="shrink-0"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          {/* Email capture section */}
          {!emailSent ? (
            <div className="space-y-3 pt-2">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  <Mail className="inline h-4 w-4 mr-1" />
                  Email me this offer
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sendingEmail}
                />
                <Button 
                  onClick={handleEmailOffer} 
                  disabled={sendingEmail || !email}
                  className="shrink-0"
                >
                  {sendingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Send"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-2">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Check className="h-5 w-5" />
                <span className="font-medium">Offer sent to your email!</span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <Button asChild className="w-full">
              <Link to="/join">Claim Offer</Link>
            </Button>
            <Button variant="ghost" onClick={handleDismiss} className="w-full">
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
